import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// A hydroelectric dam: a curved concrete wall holding back a blue reservoir on
// the high side, with spillway gates, a roadway along the crest and white water
// pouring out at the foot. Reads as a dam from the 45-degree camera by stepping
// the wall back as it rises.
const CONCRETE = "#c3c0b6";
const SHADE = "#a8a59b";
const WATER = "#2f6f9e";
const FOAM = "#dfeef4";
const ROAD = "#5a5a5e";

export const hydroDam = defineModel({
  id: "hydro-dam",
  batchId: "electric",
  name: "Hydroelectric dam",
  tags: ["building", "electric", "power", "energy", "hydro", "water", "renewable", "dam"],
  summary: "A concrete dam holding back a reservoir, generating from falling water.",
  footprint: 1.1,
  parts(): Part[] {
    return [
      // Reservoir behind the wall (-Z, the high side): a thin sheet of water
      // sitting low against the upstream face, not a tall block.
      { geometry: new THREE.BoxGeometry(1.95, 0.12, 0.95), color: WATER, position: [0, 0.62, -0.62] },
      { geometry: new THREE.BoxGeometry(1.95, 0.1, 0.02), color: "#3f86b6", position: [0, 0.6, -0.16] },
      // The dam wall: the dominant vertical mass, battered (leaning back) so the
      // downstream face slopes toward the camera and reads as a dam.
      { geometry: new THREE.BoxGeometry(1.95, 0.82, 0.34), color: CONCRETE, position: [0, 0.41, 0.06], rotation: [-0.22, 0, 0] },
      { geometry: new THREE.BoxGeometry(1.95, 0.22, 0.5), color: SHADE, position: [0, 0.11, 0.04] },
      // Roadway with kerb along the crest.
      { geometry: new THREE.BoxGeometry(2.0, 0.07, 0.42), color: ROAD, position: [0, 0.78, -0.06] },
      { geometry: new THREE.BoxGeometry(2.0, 0.06, 0.03), color: "#cfcfcf", position: [0, 0.83, 0.13] },
      { geometry: new THREE.BoxGeometry(2.0, 0.06, 0.03), color: "#cfcfcf", position: [0, 0.83, -0.25] },
      // Spillway piers and gates stepping down the downstream face.
      { geometry: new THREE.BoxGeometry(0.07, 0.66, 0.16), color: SHADE, position: [-0.55, 0.36, 0.18], rotation: [-0.22, 0, 0] },
      { geometry: new THREE.BoxGeometry(0.07, 0.66, 0.16), color: SHADE, position: [0.0, 0.36, 0.18], rotation: [-0.22, 0, 0] },
      { geometry: new THREE.BoxGeometry(0.07, 0.66, 0.16), color: SHADE, position: [0.55, 0.36, 0.18], rotation: [-0.22, 0, 0] },
      { geometry: new THREE.BoxGeometry(0.4, 0.5, 0.04), color: "#33424b", position: [-0.27, 0.4, 0.2], rotation: [-0.22, 0, 0] },
      { geometry: new THREE.BoxGeometry(0.4, 0.5, 0.04), color: "#33424b", position: [0.28, 0.4, 0.2], rotation: [-0.22, 0, 0] },
      // White water churning at the foot, downstream (+Z).
      { geometry: new THREE.BoxGeometry(1.7, 0.06, 0.4), color: "#2f6f9e", position: [0, 0.03, 0.42] },
      { geometry: new THREE.BoxGeometry(1.5, 0.05, 0.22), color: FOAM, position: [0, 0.07, 0.3] },
      { geometry: new THREE.SphereGeometry(0.1, 7, 5), color: FOAM, position: [-0.3, 0.1, 0.34], scale: [1, 0.6, 1] },
      { geometry: new THREE.SphereGeometry(0.09, 7, 5), color: "#f2fafc", position: [0.32, 0.09, 0.36], scale: [1, 0.6, 1] },
      { geometry: new THREE.SphereGeometry(0.08, 7, 5), color: FOAM, position: [0.02, 0.1, 0.46], scale: [1, 0.6, 1] },
    ];
  },
  animations: [],
});
